import { Injectable } from "@angular/core";
import { Certification as CertificationDto } from "src/app/proxy/certifications/models";
import { Certification } from "../../entities/certification";
import { LocalDbService } from "../local-db-service";

@Injectable({ providedIn: 'root' })
export class CertificationRepository {
  private get table() {
    return this.db.certificationsTable;
  }

  constructor(private readonly db: LocalDbService) {}

  async getAll(): Promise<Certification[]> {
    const rows = await this.table.toArray();
    return this.mapRows(rows);
  }

  async getAllByProjectId(projectId: number): Promise<Certification[]> {
    const rows = await this.table.where('projectId').equals(projectId).toArray();
    return this.mapRows(rows);
  }

  async getAllByRoadId(roadId: number): Promise<Certification[]> {
    const rows = await this.table.where('roadId').equals(roadId).toArray();
    return this.mapRows(
      rows
        .filter(row => row.isRejected !== true)
        .sort((a, b) => {
          const aTimestamp = Date.parse(a.date ?? '');
          const bTimestamp = Date.parse(b.date ?? '');
          const aIsValid = Number.isFinite(aTimestamp);
          const bIsValid = Number.isFinite(bTimestamp);

          if (aIsValid && bIsValid) {
            return bTimestamp - aTimestamp;
          }

          if (aIsValid) {
            return -1;
          }

          return bIsValid ? 1 : 0;
        }),
    );
  }

  async getByOriginId(originId: string): Promise<Certification | undefined> {
    const row = await this.table.get(originId);
    return row ? new Certification(row) : undefined;
  }

  async create(certification: Certification | CertificationDto): Promise<string> {
    const row = new Certification(certification);
    await this.table.add(row);
    return row.originId;
  }

  async upsertBulk(certifications: Array<Certification | CertificationDto>): Promise<void> {
    if (certifications.length === 0) {
      return;
    }

    const rows = certifications.map(certification => new Certification(certification));
    await this.table.bulkPut(rows);
  }

  async rejectByOriginId(originId: string): Promise<void> {
    await this.table.update(originId, { isRejected: true });
  }

  async setPendingSendByOriginId(originId: string, pendingSend: boolean): Promise<void> {
    await this.table.update(originId, { pendingSend });
  }

  async deleteByOriginId(originId: string): Promise<void> {
    await this.table.delete(originId);
  }

  async clear(): Promise<void> {
    await this.table.clear();
  }

  async getLatestCertificationDateByProjectId(projectId: number): Promise<string | undefined> {
    const certifications = await this.getAllByProjectId(projectId);

    return certifications
      .filter(certification => certification.date)
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0]
      ?.date;
  }

  async getLatestCertificationDateByRoadId(roadId: number): Promise<string | undefined> {
    const certifications = await this.getAllByRoadId(roadId);

    return certifications
      .filter(certification => certification.date)
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0]
      ?.date;
  }

  private mapRows(rows: Certification[]): Certification[] {
    return rows.map(row => new Certification(row));
  }

  async getPendingSendCount(): Promise<number> {
    return this.table.filter(certification => certification.pendingSend === true).count();
  }

  async getPendingSendCertifications(): Promise<Certification[]> {
    const rows = await this.table.filter(certification => certification.pendingSend === true).toArray();
    return rows.map(row => new Certification(row));
  }
}
